import React, { useEffect, useState } from "react";
import { PropsWithoutRef } from "react";
import {
  Drawer,
  Toolbar,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  IconButton,
  Collapse,
  Tooltip,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import { SIDEBAR_WIDTH } from "../constants";
import useAuth from "../hooks/useAuth";

interface Props {
  mobileOpen: boolean;
  handleSidebarToggle: () => void;
}

export default function Sidebar({
  mobileOpen,
  handleSidebarToggle,
}: PropsWithoutRef<Props>) {
  const location = useLocation();

  const navigate = useNavigate();
  const user = useAuth();
  const isPermission = (permissionName: string) => {
    if (user?.role === 1) {
      return true;
    } else {
      if (permissionName?.includes("admin")) return false;

      const isValid = user?.sub_role?.permissions?.some(
        (permission: { module: string }) =>
          permission?.module === permissionName
      );
      return isValid;
    }
  };
  console.log(user, "DATA");
  const [activeCollapse, setActiveCollapse] = useState<string | null>(null);
  const handleCollapseToggle = (key: string) => {
    setActiveCollapse((prev) => (prev === key ? null : key));
  };
  const getSidebarStatus = (pathArrays: string[], currentPathname: string) => {
    return pathArrays.some((path: string) => currentPathname.includes(path));
  };
  useEffect(() => {
    if (
      getSidebarStatus(
        [
          "/voice-library",
          "/multilingual-engine",
          "/mood-mapping",
          "/voice-library/add",
          "/mood-mapping/add-mood-map",
        ],
        location.pathname
      )
    ) {
      setActiveCollapse("behavior");
    } else if (
      getSidebarStatus(
        ["/manage-budget-categories", "/manage-alert-configuration"],
        location.pathname
      )
    ) {
      setActiveCollapse("budget");
    } else if (
      getSidebarStatus(
        [
          "/hydration",
          "/hydration/add",
          "/reminder-engine",
          "/reminder-engine/add",
        ],
        location.pathname
      )
    ) {
      setActiveCollapse("health");
    } else if (
      getSidebarStatus(
        [
          "/translation-messaging",
          "/translation-messaging/view",
          "/smart-messaging",
        ],
        location.pathname
      )
    ) {
      setActiveCollapse("messaging");
    } else if (
      getSidebarStatus(
        ["/user-engagement", "/wellness-lifestyle", "/financial-overview"],
        location.pathname
      )
    ) {
      setActiveCollapse("analytics");
    } else if (
      getSidebarStatus(
        [
          "/manage-users",
          "/reset-password-requests",
          "/manage-users/edit",
          "/manage-users/detail",
        ],
        location.pathname
      )
    ) {
      setActiveCollapse("users");
    } else {
      setActiveCollapse(null);
    }
  }, [location.pathname]);

  const drawer = (
    <>
      <Toolbar className="sidebar-logo" sx={{ position: "relative" }}>
        <figure>
          <img
            src="/static/images/ozri_logo.svg"
            alt=""
            onClick={() => navigate("/dashboard")}
          />
        </figure>
        <IconButton
          sx={{
            display: "none",
            position: "absolute",
            top: "10px",
            right: "10px",
          }}
          onClick={handleSidebarToggle}
        >
          <MenuIcon />
        </IconButton>
      </Toolbar>

      <List sx={{ flexGrow: 1 }} className="sidebr-lst">
        {isPermission("Dashboard") && (
          <Tooltip title="Dashboard" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={location.pathname.includes("/dashboard")}
              onClick={() => navigate("/dashboard")}
            >
              <ListItemIcon>
                <img src="/static/images/dashboard_icon.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText className="lstitm-txt" primary="Dashboard" />
            </ListItemButton>
          </Tooltip>
        )}

        {isPermission("User Management") && (
          <>
            <Tooltip title="User Management" placement="right">
              <ListItemButton
                className="lst-itm"
                onClick={() => handleCollapseToggle("users")}
              >
                <ListItemIcon>
                  <img src="/static/images/user_icon.svg" alt="Icon" />
                </ListItemIcon>
                <ListItemText
                  className="lstitm-txt"
                  primary="User Management"
                />
                {activeCollapse === "users" ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </Tooltip>
            <Collapse
              in={activeCollapse === "users"}
              timeout="auto"
              unmountOnExit
            >
              <List component="div" disablePadding>
                <Tooltip title="Users" placement="right">
                  <ListItemButton
                    className="lst-itm"
                    selected={location.pathname.includes("/manage-users")}
                    onClick={() => navigate("/manage-users")}
                  >
                    <ListItemText primary="Users" />
                  </ListItemButton>
                </Tooltip>
                <Tooltip title="Reset Password Users" placement="right">
                  <ListItemButton
                    className="lst-itm"
                    selected={location.pathname.includes(
                      "/reset-password-requests"
                    )}
                    onClick={() => navigate("/reset-password-requests")}
                  >
                    <ListItemText primary="Reset Password Users" />
                  </ListItemButton>
                </Tooltip>
              </List>
            </Collapse>
          </>
        )}
        {isPermission("Family / Shared Account") && (
          <Tooltip title="Family / Shared Account" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={
                location.pathname.includes("/manage-shared-accounts") ||
                location.pathname.includes("/manage-shared-accounts/view")
              }
              onClick={() => navigate("/manage-shared-accounts")}
            >
              <ListItemIcon>
                <img src="/static/images/people_icon.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText
                className="lstitm-txt"
                primary="Family / Shared Account"
              />
            </ListItemButton>
          </Tooltip>
        )}

        {isPermission("AI Behavior & Language") && (
          <>
            <Tooltip title="AI Behavior & Language" placement="right">
              <ListItemButton
                className="lst-itm"
                onClick={() => handleCollapseToggle("behavior")}
              >
                <ListItemIcon>
                  <img src="/static/images/ai.svg" alt="Icon" />
                </ListItemIcon>
                <ListItemText
                  className="lstitm-txt"
                  primary="AI Behavior & Language"
                />
                {activeCollapse === "behavior" ? (
                  <ExpandLess />
                ) : (
                  <ExpandMore />
                )}
              </ListItemButton>
            </Tooltip>
            <Collapse
              in={activeCollapse === "behavior"}
              timeout="auto"
              unmountOnExit
            >
              <List component="div" disablePadding>
                <Tooltip title="Voice Library" placement="right">
                  <ListItemButton
                    className="lst-itm"
                    selected={
                      location.pathname.includes("/voice-library") ||
                      location.pathname.includes("/voice-library/add")
                    }
                    onClick={() => navigate("/voice-library")}
                  >
                    <ListItemText primary="Voice Library" />
                  </ListItemButton>
                </Tooltip>
                <Tooltip title="Multilingual Engine" placement="right">
                  <ListItemButton
                    className="lst-itm"
                    selected={location.pathname.includes(
                      "/multilingual-engine"
                    )}
                    onClick={() => navigate("/multilingual-engine")}
                  >
                    <ListItemText primary="Multilingual Engine" />
                  </ListItemButton>
                </Tooltip>
                {/* <Tooltip title="Emotional AI & Mood Mapping" placement="right">
                  <ListItemButton
                    className="lst-itm"
                    selected={
                      location.pathname.includes("/mood-mapping") ||
                      location.pathname.includes("/mood-mapping/add-mood-map")
                    }
                    onClick={() => navigate("/mood-mapping")}
                  >
                    <ListItemText primary="Emotional AI & Mood Mapping" />
                  </ListItemButton>
                </Tooltip> */}
              </List>
            </Collapse>
          </>
        )}

        {isPermission("Subscription & Plan") && (
          <Tooltip title="Subscription & Plan" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={location.pathname.includes("/manage-subscriptions")}
              onClick={() => navigate("/manage-subscriptions")}
            >
              <ListItemIcon>
                <img src="/static/images/subscription_icon.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText
                className="lstitm-txt"
                primary="Subscription & Plan"
              />
            </ListItemButton>
          </Tooltip>
        )}

        {isPermission("Analytics & Insights") && (
          <>
            <Tooltip title="Analytics & Insights" placement="right">
              <ListItemButton
                className="lst-itm"
                onClick={() => handleCollapseToggle("analytics")}
              >
                <ListItemIcon>
                  <img src="/static/images/status_icon.svg" alt="Icon" />
                </ListItemIcon>
                <ListItemText
                  className="lstitm-txt"
                  primary="Analytics & Insights"
                />
                {activeCollapse === "analytics" ? (
                  <ExpandLess />
                ) : (
                  <ExpandMore />
                )}
              </ListItemButton>
            </Tooltip>
            <Collapse
              in={activeCollapse === "analytics"}
              timeout="auto"
              unmountOnExit
            >
              <List component="div" disablePadding>
                <Tooltip title="User Engagement Dashboard" placement="right">
                  <ListItemButton
                    className="lst-itm"
                    selected={location.pathname.includes("/user-engagement")}
                    onClick={() => navigate("/user-engagement")}
                  >
                    <ListItemText primary="User Engagement Dashboard" />
                  </ListItemButton>
                </Tooltip>
                <Tooltip title="Wellness & Lifestyle Trends" placement="right">
                  <ListItemButton
                    className="lst-itm"
                    selected={location.pathname.includes("/wellness-lifestyle")}
                    onClick={() => navigate("/wellness-lifestyle")}
                  >
                    <ListItemText primary="Wellness & Lifestyle Trends" />
                  </ListItemButton>
                </Tooltip>
                <Tooltip title="Financial Overview" placement="right">
                  <ListItemButton
                    className="lst-itm"
                    selected={location.pathname.includes("/financial-overview")}
                    onClick={() => navigate("/financial-overview")}
                  >
                    <ListItemText primary="Financial Overview" />
                  </ListItemButton>
                </Tooltip>
              </List>
            </Collapse>
          </>
        )}

        {isPermission("Budget Intelligence") && (
          <>
            <Tooltip title="Budget Intelligence" placement="right">
              <ListItemButton
                className="lst-itm"
                onClick={() => handleCollapseToggle("budget")}
              >
                <ListItemIcon>
                  <img src="/static/images/budget_icon.svg" alt="Icon" />
                </ListItemIcon>
                <ListItemText
                  className="lstitm-txt"
                  primary="Budget Intelligence"
                />
                {activeCollapse === "budget" ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </Tooltip>
            <Collapse
              in={activeCollapse === "budget"}
              timeout="auto"
              unmountOnExit
            >
              <List component="div" disablePadding>
                <Tooltip title="Budget Categories" placement="right">
                  <ListItemButton
                    className="lst-itm"
                    selected={location.pathname.includes(
                      "/manage-budget-categories"
                    )}
                    onClick={() => navigate("/manage-budget-categories")}
                  >
                    <ListItemText primary="Budget Categories" />
                  </ListItemButton>
                </Tooltip>
                <Tooltip title="Alerts Configuration" placement="right">
                  <ListItemButton
                    className="lst-itm"
                    selected={location.pathname.includes(
                      "/manage-alert-configuration"
                    )}
                    onClick={() => navigate("/manage-alert-configuration")}
                  >
                    <ListItemText primary="Alerts Configuration" />
                  </ListItemButton>
                </Tooltip>
              </List>
            </Collapse>
          </>
        )}

        {isPermission("Document & OCR Processing") && (
          <Tooltip title="Document & OCR Processing" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={
                location.pathname.includes("/document-processing") ||
                location.pathname.includes("/document-processing/add-document")
              }
              onClick={() => navigate("/document-processing")}
            >
              <ListItemIcon>
                <img src="/static/images/document_icon.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText
                className="lstitm-txt"
                primary="Document & OCR Processing"
              />
            </ListItemButton>
          </Tooltip>
        )}

        {isPermission("Health & Habit Configuration") && (
          <>
            <Tooltip title="Health & Habit Configuration" placement="right">
              <ListItemButton
                className="lst-itm"
                onClick={() => handleCollapseToggle("health")}
              >
                <ListItemIcon>
                  <img src="/static/images/health_icon.svg" alt="Icon" />
                </ListItemIcon>
                <ListItemText
                  className="lstitm-txt"
                  primary="Health & Habit Configuration"
                />
                {activeCollapse === "health" ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </Tooltip>
            <Collapse
              in={activeCollapse === "health"}
              timeout="auto"
              unmountOnExit
            >
              <List component="div" disablePadding>
                <Tooltip title="Hydration & Goal Settings" placement="right">
                  <ListItemButton
                    className="lst-itm"
                    selected={
                      location.pathname.includes("/hydration") ||
                      location.pathname.includes("/hydration/add")
                    }
                    onClick={() => navigate("/hydration")}
                  >
                    <ListItemText primary="Hydration & Goal Settings" />
                  </ListItemButton>
                </Tooltip>
                <Tooltip title="Reminder Engine" placement="right">
                  <ListItemButton
                    className="lst-itm"
                    selected={
                      location.pathname.includes("/reminder-engine") ||
                      location.pathname.includes("/reminder-engine/add")
                    }
                    onClick={() => navigate("/reminder-engine")}
                  >
                    <ListItemText primary="Reminder Engine" />
                  </ListItemButton>
                </Tooltip>
              </List>
            </Collapse>
          </>
        )}

        {isPermission("Shopping & Meal AI") && (
          <Tooltip title="Shopping & Meal AI" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={
                location.pathname.includes("/shopping-meals") ||
                location.pathname.includes("/shopping-meals/sub-categories")
              }
              onClick={() => navigate("/shopping-meals")}
            >
              <ListItemIcon>
                <img src="/static/images/category_icon.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText
                className="lstitm-txt"
                primary="Shopping & Meal AI"
              />
            </ListItemButton>
          </Tooltip>
        )}

        {/* {isPermission("Label Recognition Editor") && (
          <Tooltip title="Label Recognition Editor" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={location.pathname.includes("/label-recognition")}
              onClick={() => navigate("/label-recognition")}
            >
              <ListItemIcon>
                <img src="/static/images/label_icon.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText
                className="lstitm-txt"
                primary="Label Recognition Editor"
              />
            </ListItemButton>
          </Tooltip>
        )} */}

        {/* {isPermission("Location Intelligence") && (
          <Tooltip title="Location Intelligence" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={
                location.pathname.includes("/location-intelligence") ||
                location.pathname.includes(
                  "/location-intelligence/add-location"
                )
              }
              onClick={() => navigate("/location-intelligence")}
            >
              <ListItemIcon>
                <img src="/static/images/location_icon.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText
                className="lstitm-txt"
                primary="Location Intelligence"
              />
            </ListItemButton>
          </Tooltip>
        )} */}

        {isPermission("Translation & Messaging") && (
          <>
            <Tooltip title="Translation & Messaging" placement="right">
              <ListItemButton
                className="lst-itm"
                onClick={() => handleCollapseToggle("messaging")}
              >
                <ListItemIcon>
                  <img src="/static/images/translation_icon.svg" alt="Icon" />
                </ListItemIcon>
                <ListItemText
                  className="lstitm-txt"
                  primary="Translation & Messaging"
                />
                {activeCollapse === "health" ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </Tooltip>
            <Collapse
              in={activeCollapse === "messaging"}
              timeout="auto"
              unmountOnExit
            >
              <List component="div" disablePadding>
                <Tooltip title="Language Library" placement="right">
                  <ListItemButton
                    className="lst-itm"
                    selected={
                      location.pathname.includes("/translation-messaging") ||
                      location.pathname.includes("/translation-messaging/view")
                    }
                    onClick={() => navigate("/translation-messaging")}
                  >
                    <ListItemText primary="Language Library" />
                  </ListItemButton>
                </Tooltip>
                <Tooltip title="Smart Messaging Templates" placement="right">
                  <ListItemButton
                    className="lst-itm"
                    selected={location.pathname.includes("/smart-messaging")}
                    onClick={() => navigate("/smart-messaging")}
                  >
                    <ListItemText primary="Smart Messaging Templates" />
                  </ListItemButton>
                </Tooltip>
              </List>
            </Collapse>
          </>
        )}

        {isPermission("Notification Management") && (
          <Tooltip title="Notification Management" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={location.pathname.includes("/manage-notifications")}
              onClick={() => navigate("/manage-notifications")}
            >
              <ListItemIcon>
                <img src="/static/images/notification2_icon.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText
                className="lstitm-txt"
                primary="Notification Management"
              />
            </ListItemButton>
          </Tooltip>
        )}

        {isPermission("Revenue Management") && (
          <Tooltip title="Revenue Management" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={location.pathname.includes("/manage-revenue")}
              onClick={() => navigate("/manage-revenue")}
            >
              <ListItemIcon>
                <img src="/static/images/money_icon.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText
                className="lstitm-txt"
                primary="Revenue Management"
              />
            </ListItemButton>
          </Tooltip>
        )}

        {isPermission("Manage Customer Support") && (
          <Tooltip title="Manage Customer Support" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={location.pathname.includes("/manage-customer-support")}
              onClick={() => navigate("/manage-customer-support")}
            >
              <ListItemIcon>
                <img src="/static/images/support_icon.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText
                className="lstitm-txt"
                primary="Manage Customer Support"
              />
            </ListItemButton>
          </Tooltip>
        )}

        {isPermission("Manage Sub-Admin") && (
          <Tooltip title="Manage Sub-Admin" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={
                location.pathname.includes("/manage-subadmin") ||
                location.pathname.includes("/manage-subadmin/add") ||
                location.pathname.includes("/manage-subadmin/detail") ||
                location.pathname.includes("/manage-subadmin/roles") ||
                location.pathname.includes("/manage-subadmin/roles/add") ||
                location.pathname.includes("/manage-subadmin/roles/detail")
              }
              onClick={() => navigate("/manage-subadmin")}
            >
              <ListItemIcon>
                <img src="/static/images/user_icon.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText className="lstitm-txt" primary="Manage Sub-Admin" />
            </ListItemButton>
          </Tooltip>
        )}

        {isPermission("Manage CMS Pages") && (
          <Tooltip title="Manage CMS Pages" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={location.pathname.includes("/manage-cms")}
              onClick={() => navigate("/manage-cms")}
            >
              <ListItemIcon>
                <img src="/static/images/cms_icon.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText className="lstitm-txt" primary="Manage CMS Pages" />
            </ListItemButton>
          </Tooltip>
        )}

        {isPermission("Settings") && (
          <Tooltip title="Settings" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={location.pathname.includes("/settings")}
              onClick={() => navigate("/settings")}
            >
              <ListItemIcon>
                <img src="/static/images/settings.svg" alt="Icon" />
              </ListItemIcon>
              <ListItemText className="lstitm-txt" primary="Settings" />
            </ListItemButton>
          </Tooltip>
        )}
      </List>
    </>
  );
  return (
    <Box
      component="nav"
      sx={{
        width: mobileOpen ? SIDEBAR_WIDTH : 0,
        transition: "width 0.3s",
        overflowX: "hidden",
      }}
      className={mobileOpen ? "sidebar-main-div active" : "sidebar-main-div"}
    >
      <Drawer
        className="sidebar_drawer"
        variant="persistent"
        sx={{
          // display: { xs: "none", lg: "block" },
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            boxSizing: "border-box",
          },
        }}
        open
      >
        {drawer}
      </Drawer>
      {/* <Drawer
        variant="permanent"
        className="sidebar_drawer"
        sx={{
          display: { xs: "none", lg: "block" },
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            boxSizing: "border-box",
          },
        }}
        open
      >
        {drawer}
      </Drawer> */}
    </Box>
  );
}
